import { useEffect, useState } from 'react'
import { useOutletContext } from 'react-router'
import { PageFooter } from '@/components/commons/page-footer'
import { PageHero } from '@/components/commons/page-hero'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ButtonLinkLegacy } from '@/components/ui/button-link-legacy'
import {
	ArrowLeftIcon,
	DownloadCloud02Icon,
	ImageDownIcon,
	ImageUpIcon
} from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { ManualRetouchingCard } from '@/features/photoset/components/manual-retouching-card'
import { ManualRetouchingUploader } from '@/features/photoset/components/manual-retouching-uploader'
import {
	PhotosetStepCardsContainer,
	PhotosetStepWrapper
} from '@/features/photoset/components/photoset-step-atoms'
import { StepHeroControls } from '@/features/photoset/components/step-hero-controls'
import { useGetPhotoset } from '@/features/photoset/queries/use-get-photoset'
import { useUpdateStepToAiManualRetouching } from '@/features/photoset/queries/use-update-step-to-ai-manual-retouching'
import { useUpdateStepToAiVetting } from '@/features/photoset/queries/use-update-step-to-ai-vetting'
import type { PhotosetURLParams } from '@/features/photoset/types'
import { errorHandler } from '@/helpers/utils'
import { QUERY_KEYS, queryClient } from '@/query-client'
import { useTranslation } from '@/translations/i18n'

export const PhotosetAiManualRetouchingUploadStep = () => {
	const { t } = useTranslation()
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const [isGenerationThumbsCompleted, setIsGenerationThumbsCompleted] =
		useState(false)

	// queries
	const { data: photoset } = useGetPhotoset(
		{ id: urlParams.id },
		{
			refetchInterval: () =>
				isGenerationThumbsCompleted ? false : 10_000
		}
	)

	// mutations
	const {
		mutate: updateStepToAiVetting,
		isPending: isPendingUpdateStepToAiVetting
	} = useUpdateStepToAiVetting({
		onError: (error) => errorHandler(error),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.PHOTOSET]
			})
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.PHOTOSET_SPACES]
			})
		}
	})

	const updateStepToAiManualRetouchingMutation =
		useUpdateStepToAiManualRetouching({
			onError: (error) => errorHandler(error),
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: [QUERY_KEYS.PHOTOSET]
				})
				queryClient.invalidateQueries({
					queryKey: [QUERY_KEYS.PHOTOSET_SPACES]
				})
			}
		})

	useEffect(() => {
		if (photoset?.need_retouching_zip_url) {
			setIsGenerationThumbsCompleted(true)
		}
	}, [photoset?.need_retouching_zip_url])

	return (
		<>
			<PageHero
				endSlot={<StepHeroControls hideSortControls />}
				subtitle={t('photoset:ai_manual_retouching.subtitle')}
				title={t('photoset:ai_manual_retouching.title')}
			/>
			<PhotosetStepWrapper>
				<PhotosetStepCardsContainer>
					<ManualRetouchingCard
						endSlot={
							<div className='flex flex-col items-center gap-3 [&>p]:max-w-[365px]'>
								<ButtonLinkLegacy
									disabled={
										!photoset?.need_retouching_zip_url
									}
									to={photoset?.need_retouching_zip_url ?? {}}
								>
									{photoset?.need_retouching_zip_url ? (
										<>
											<DownloadCloud02Icon size={20} />
											<span>
												{t('common:download_image', {
													count: photoset?.need_retouching_zip_images_count
												})}
											</span>
										</>
									) : (
										<>
											<Spinner size={20} />
											<span>
												{t('common:creating_zip')}
											</span>
										</>
									)}
								</ButtonLinkLegacy>
								<p>{t('common:download_pending_paragraph')}</p>
							</div>
						}
						icon={<ImageDownIcon size={40} />}
						paragraph={t(
							'photoset:ai_manual_retouching.download_paragraph'
						)}
						subtitle={t(
							'photoset:ai_manual_retouching.download_subtitle'
						)}
						title={t('common:download_title')}
					/>

					<ManualRetouchingCard
						endSlot={
							<ManualRetouchingUploader
								isDisabled={!photoset?.need_retouching_zip_url}
								onUploadCb={
									updateStepToAiManualRetouchingMutation.mutateAsync
								}
								photosetId={urlParams.id}
							/>
						}
						icon={<ImageUpIcon size={40} />}
						paragraph={t('common:upload_jpgs_name_info')}
						subtitle={t(
							'photoset:ai_manual_retouching.upload_subtitle'
						)}
						title={t('common:upload_retouched_images')}
					/>
				</PhotosetStepCardsContainer>

				<PageFooter
					startSlot={
						<ButtonLegacy
							disabled={isPendingUpdateStepToAiVetting}
							onClick={() => updateStepToAiVetting(urlParams.id)}
							type='button'
							variant='tertiary'
						>
							<ArrowLeftIcon />
							{t('photoset:ai_vetting.title')}
						</ButtonLegacy>
					}
				/>
			</PhotosetStepWrapper>
		</>
	)
}
